import { useState, type ReactNode } from "react";
import { Button } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";

type ProductSummaryCardProps = {
  productName: string;
  productRef: string;
  status: "active" | "hidden" | "out_of_stock";
  stockCount: number;
  priceLabel: string;
  firstImageUrl?: string;
  shareButton?: ReactNode;
  previewHref?: string;
};

const STATUS_DOT_CLASS: Record<ProductSummaryCardProps["status"], string> = {
  active: "bg-[color:var(--success-500)]",
  hidden: "bg-[color:var(--accent-500)]",
  out_of_stock: "bg-[color:var(--error-500)]",
};

function EyeIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/**
 * Dark-navy product summary rendered inside the page hero's `heroAux`
 * slot. Matches the Claude Design edit-product hero summary row:
 * 44px thumbnail + bilingual name · mono price on row 1,
 * mono SKU · status dot+label · stock count on row 2,
 * Preview (ghost) + Send-to-buyer (solid white) actions on the right.
 */
export function ProductSummaryCard({
  productName,
  status,
  stockCount,
  priceLabel,
  firstImageUrl,
  shareButton,
  previewHref,
}: ProductSummaryCardProps) {
  const t = useTranslations();
  const statusLabel =
    status === "active"
      ? t(keys.products.editSidebarPublishingPublished)
      : status === "hidden"
        ? t(keys.products.editSidebarPublishingHidden)
        : t(keys.products.editSidebarInventoryOutOfStock);

  const previewButton = previewHref ? (
    <a
      href={previewHref}
      target="_blank"
      rel="noreferrer"
      className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-meta font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)]"
    >
      <EyeIcon />
      {t(keys.products.editSummaryPreview)}
    </a>
  ) : (
    <Button
      type="button"
      variant="outline"
      disabled
      leadingIcon={<EyeIcon />}
      className="h-9 gap-1.5 rounded-lg border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-meta font-medium text-[color:var(--text-disabled)]"
    >
      {t(keys.products.editSummaryPreview)}
    </Button>
  );

  return (
    <div className="flex flex-wrap items-center gap-3.5 text-[color:var(--text-primary)]">
      <ProductSummaryThumbnail firstImageUrl={firstImageUrl} />


      <div className="flex min-w-0 flex-col gap-1">
        <span className="truncate text-[17px] font-bold leading-tight tracking-[-0.01em] text-[color:var(--text-primary)]">
          {productName}
        </span>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] font-medium text-[color:var(--text-muted)]">
          <span className="font-semibold text-[color:var(--text-secondary)]">
            {priceLabel}
          </span>
          <span aria-hidden="true" className="opacity-40">
            ·
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT_CLASS[status]}`}
            />
            {statusLabel}
          </span>
          <span aria-hidden="true" className="opacity-40">
            ·
          </span>
          <span>
            <span className="font-mono">{stockCount}</span>{" "}
            {t(keys.products.editSummaryInStock)}
          </span>
        </div>
      </div>

      <div className="ms-auto flex shrink-0 items-center gap-2">
        {previewButton}
        {shareButton ? (
          <div className="product-summary-share">{shareButton}</div>
        ) : null}
      </div>
    </div>
  );
}

function ProductSummaryThumbnail({
  firstImageUrl,
}: {
  firstImageUrl?: string;
}) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(firstImageUrl) && !failed;
  return (
    <div className="relative flex h-[52px] w-[52px] shrink-0 items-center justify-center overflow-hidden rounded-[13px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] text-[color:var(--text-disabled)]">
      {showImage ? (
        <img
          src={firstImageUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M8 8V6a4 4 0 0 1 8 0v2" />
          <rect x="5" y="8" width="14" height="13" rx="2.5" />
          <path d="M9 13h6" />
        </svg>
      )}
    </div>
  );
}
